import { useState, useEffect, useMemo } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { Globe, Layers, BookOpen, Search, Send, KeyRound, Bot } from "lucide-react";
import { useOrganization } from "@clerk/clerk-react";
import * as api from "../lib/api";
import { SettingsContext, EMPTY_FORM, formFromSettings, PRIMARY } from "../components/settings/shared";

// Sub-routes of /dashboard/settings (see App.jsx). Each section renders its own
// form, but they all edit and save the same settings row.
const SECTIONS = [
  { to: "site", icon: Globe, label: "Site & Sync", hint: "Content source, live URLs, auto-sync" },
  { to: "cms", icon: Layers, label: "CMS Integrations", hint: "Supported CMS + setup guides" },
  { to: "business", icon: BookOpen, label: "Business Context", hint: "What we do, personas, key pages, competitors" },
  { to: "search-console", icon: Search, label: "Search Console", hint: "Google OAuth + property" },
  { to: "indexing", icon: Send, label: "Bulk Indexing", hint: "Submit URLs to Google" },
  { to: "api-keys", icon: KeyRound, label: "API Keys", hint: "Serper search, DeepL translation, OpenAI article AI" },
  { to: "integrations", icon: Bot, label: "Integrations", hint: "Integration key, MCP, webhook" },
];

export default function Settings() {
  const { organization } = useOrganization();
  const [form, setForm] = useState(EMPTY_FORM);
  const [savedForm, setSavedForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [gscConnected, setGscConnected] = useState(false);
  const [cmsConn, setCmsConn] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.getSettings(), api.getSyncSchedule(), api.getGscSettings(), api.getCmsConnection().catch(() => null)])
      .then(([s, sched, gsc, cms]) => {
        setCmsConn(cms);
        const loaded = formFromSettings(s);
        setForm(loaded);
        setSavedForm(loaded);
        setSchedule(sched);
        setGscConnected(gsc?.verified && gsc?.configured);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [organization?.id]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(savedForm), [form, savedForm]);

  const handleChange = e => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [e.target.name]: val }));
  };

  // Saves the full form (every section's edits) and returns a result for the banner.
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, sync_interval_hours: Number(form.sync_interval_hours) };
      await api.saveSettings(payload);
      setSavedForm(payload);
      setSchedule(await api.getSyncSchedule());
      return { success: true, message: "Settings saved successfully." };
    } catch (err) {
      console.error("[Settings] Save settings failed:", {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
        hint: err.response?.status === 401
          ? "Auth rejected — token may lack org_id claim. Select an org in Clerk."
          : err.response?.status === 403
            ? "RLS blocked — org_id in JWT doesn't match any org_id in settings table."
            : "Check backend logs for Supabase RLS or auth errors.",
      });
      return { success: false, message: err.response?.data?.detail || err.message || "Failed to save settings." };
    } finally {
      setSaving(false);
    }
  };

  // For fields the backend persisted on its own (e.g. a generated integration key).
  const applySaved = patch => {
    setForm(f => ({ ...f, ...patch }));
    setSavedForm(f => ({ ...f, ...patch }));
  };

  const refreshCms = async () => {
    const cms = await api.getCmsConnection();
    setCmsConn(cms);
    return cms;
  };

  const ctx = {
    form, setForm, handleChange, loading, saving, save, dirty, applySaved,
    schedule, gscConnected, setGscConnected,
    cmsConn, setCmsConn, refreshCms,
  };

  return (
    <div data-testid="settings-page" className="p-6 sm:p-8 max-w-5xl mx-auto">
      <div className="mb-8 pb-6 border-b border-zinc-100">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mb-1">Configuration</p>
        <h1 className="text-4xl font-bold text-zinc-900 tracking-tight">
          Settings
        </h1>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <nav className="md:w-56 flex-shrink-0 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible -mx-1 px-1">
          {SECTIONS.map(({ to, icon: Icon, label, hint }) => (
            <NavLink
              key={to} to={to}
              data-testid={`settings-nav-${to}`}
              className={({ isActive }) =>
                `flex items-start gap-2.5 px-3 py-2.5 rounded-lg transition-colors whitespace-nowrap md:whitespace-normal ${
                  isActive ? "bg-[#018D4C]/10" : "hover:bg-zinc-50"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={15} strokeWidth={2} className="mt-0.5 flex-shrink-0" style={{ color: isActive ? PRIMARY : "#71717a" }} />
                  <div className="min-w-0">
                    <p className="text-xs font-bold" style={{ color: isActive ? PRIMARY : "#18181b" }}>{label}</p>
                    <p className="hidden md:block text-[10px] text-zinc-400 leading-snug">{hint}</p>
                  </div>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1 min-w-0">
          <SettingsContext.Provider value={ctx}>
            <Outlet />
          </SettingsContext.Provider>
        </div>
      </div>
    </div>
  );
}
